/**
 * Display formatting for the free-text tables users build in Credit Proposal
 * sections (Borrower's Proposal Review, IRS builder, Financial Performance),
 * shared by the section previews, the report and the Word export.
 *
 * A cell that holds just an amount ("5000000", "PKR 1250000.5", "(1200)",
 * "12.5%") is shown with thousands separators and right-aligned; anything else
 * is shown exactly as typed. Years ("2024"), codes with leading zeros ("0042")
 * and cells in identifier columns (Year, No., ID, Account, CNIC, …) are never
 * grouped, so "2024" does not become "2,024".
 */

export interface FormattedTableCell {
  text: string;
  numeric: boolean;
}

export interface TableColumnLike {
  title?: string | null;
  subColumns?: Array<string | null | undefined> | null;
}

// [prefix] [ "(" ] [sign] digits [.digits] [ ")" ] [suffix]
const AMOUNT_CELL =
  /^(?<prefix>(?:PKR|Rs\.?|USD|\$)\s*)?(?<open>\()?(?<sign>[-+])?(?<integer>\d{1,3}(?:,\d{3})+|\d+)(?<fraction>\.\d+)?(?<close>\))?(?<suffix>\s*(?:%|[kKmMbB]n?|mln|million|billion))?$/;

const IDENTIFIER_COLUMN =
  /(^|[^a-z])(no|number|#|ids?|code|cnic|ntn|account|acc|a\/c|phone|mobile|contact|ref|reference|serial|sr|s\.\s?no)([^a-z]|$)/i;

// A column of years ("Year", "Financial Year", "FY") — not one headed by a
// year ("FY2024", "Revenue 2024"), whose cells are amounts.
const YEAR_COLUMN = /^(financial\s+|fiscal\s+)?(years?|yrs?|fy)$/i;

function isYear(integer: string, hasDecimals: boolean): boolean {
  if (hasDecimals || integer.length !== 4 || integer.includes(',')) {
    return false;
  }

  const year = Number(integer);
  return year >= 1900 && year <= 2100;
}

export function formatTableCell(value: unknown, columnTitle?: string | null): FormattedTableCell {
  if (value === null || value === undefined) {
    return { text: '', numeric: false };
  }

  const text = String(value).trim();
  const match = AMOUNT_CELL.exec(text);

  if (!match?.groups) {
    return { text: String(value), numeric: false };
  }

  const { prefix = '', open = '', sign = '', integer, fraction = '', close = '', suffix = '' } = match.groups;

  // "(1200" or "1200)" are not amounts.
  if (Boolean(open) !== Boolean(close)) {
    return { text: String(value), numeric: false };
  }

  const digits = integer.replace(/,/g, '');
  const leadingZeroCode = digits.length > 1 && digits.startsWith('0') && !fraction;

  if (
    leadingZeroCode ||
    (!prefix && !suffix && isYear(integer, Boolean(fraction))) ||
    IDENTIFIER_COLUMN.test(columnTitle ?? '') ||
    YEAR_COLUMN.test((columnTitle ?? '').trim())
  ) {
    return { text: String(value), numeric: false };
  }

  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return {
    text: `${prefix}${open}${sign}${grouped}${fraction}${close}${suffix}`,
    numeric: true,
  };
}

/** One title per leaf column, in cell order ("Revenue 2024" for a sub-column). */
export function tableLeafTitles(columns: TableColumnLike[] | null | undefined): string[] {
  const titles: string[] = [];

  (columns ?? []).forEach((column) => {
    const title = String(column?.title ?? '').trim();
    const subColumns = column?.subColumns ?? [];

    if (subColumns.length > 0) {
      subColumns.forEach((subColumn) => titles.push(`${title} ${String(subColumn ?? '').trim()}`.trim()));
    } else {
      titles.push(title);
    }
  });

  return titles;
}
